// Thin fetch wrapper. Every call goes through /api, which Vite proxies to the
// API in development and which serves this bundle in a deployment. The model
// key stays server side and the browser never sees it.

import { accessToken, authEnabled, signIn } from './auth.js'

// Attached per request rather than once at start-up, so a token that expires
// mid-session is refreshed rather than becoming a wall of failed calls.
async function authHeaders() {
  if (!authEnabled) return {}
  const token = await accessToken()
  return token ? { authorization: `Bearer ${token}` } : {}
}

async function call(path, options = {}) {
  const headers = { 'content-type': 'application/json', ...(await authHeaders()) }

  const res = await fetch(`/api${path}`, {
    headers,
    ...options,
    body: options.body ? JSON.stringify(options.body) : undefined,
  })

  // A 401 after a token was attached means the session is genuinely gone, not
  // that this one call failed. Sending the user back to sign-in beats showing
  // an error on every screen at once.
  if (res.status === 401 && authEnabled) {
    await signIn()
    throw new Error('Your session expired. Signing you back in.')
  }

  const text = await res.text()
  const data = text ? JSON.parse(text) : null
  if (!res.ok) {
    // FastAPI reports validation failures under `detail`; the app's own errors
    // come back as `error` with a `code`.
    const message =
      data?.error ||
      (typeof data?.detail === 'string' ? data.detail : null) ||
      `Request failed (${res.status})`
    const err = new Error(message)
    err.code = data?.code
    throw err
  }
  return data
}

export const api = {
  stats: () => call('/stats'),
  accounts: () => call('/accounts'),
  account: (slug) => call(`/accounts/${slug}`),
  appendix: () => call('/appendix'),
  intel: () => call('/intel'),
  triggers: () => call('/triggers'),
  wcFit: () => call('/wc-fit'),
  procFit: () => call('/proc-fit'),
  segments: () => call('/segments'),

  tasks: () => call('/tasks'),
  task: (id) => call(`/tasks/${id}`),
  createTask: (body) => call('/tasks', { method: 'POST', body }),
  patchTask: (id, body) => call(`/tasks/${id}`, { method: 'PATCH', body }),
  deleteTask: (id) => call(`/tasks/${id}`, { method: 'DELETE' }),
  patchOccurrence: (id, body) => call(`/tasks/occurrences/${id}`, { method: 'PATCH', body }),
  setPeople: (people) => call('/people', { method: 'PUT', body: { people } }),

  // Attachments go up as raw bytes on their own route, not as JSON: base64 in
  // the body would inflate the payload and blow the JSON size limit.
  uploadAttachment: async (taskId, file, { occurrence } = {}) => {
    const q = new URLSearchParams({ name: file.name, mime: file.type || 'application/octet-stream' })
    if (occurrence) q.set('occurrence', occurrence)
    const res = await fetch(`/api/tasks/${taskId}/attachments?${q}`, {
      method: 'POST',
      headers: { 'content-type': file.type || 'application/octet-stream', ...(await authHeaders()) },
      body: file,
    })
    const data = await res.json().catch(() => null)
    if (!res.ok) throw new Error(data?.error || `Upload failed (${res.status})`)
    return data
  },
  // Fetched with the token and opened from a blob: a plain link carries no
  // bearer token, so it would 401 in a signed-in deployment.
  openAttachment: async (key, stored, name) => {
    const res = await fetch(`/api/tasks/${key}/attachments/${encodeURIComponent(stored)}`, { headers: await authHeaders() })
    if (!res.ok) throw new Error(`Could not fetch ${name || 'the file'} (${res.status})`)
    const url = URL.createObjectURL(await res.blob())
    window.open(url, '_blank', 'noopener')
    setTimeout(() => URL.revokeObjectURL(url), 60000)
  },
  deleteAttachment: (taskId, attId, occurrence) =>
    call(`/tasks/${taskId}/attachments/${attId}${occurrence ? `?occurrence=${occurrence}` : ''}`, { method: 'DELETE' }),
  setFitOverride: (slug, body) => call(`/accounts/${slug}/fit-override`, { method: 'PUT', body }),
  dismissTrigger: (id) => call(`/triggers/${encodeURIComponent(id)}/dismiss`, { method: 'POST' }),
  campaigns: () => call('/campaigns'),
  campaign: (id) => call(`/campaigns/${id}`),
  createCampaign: (body) => call('/campaigns', { method: 'POST', body }),
  buildCampaign: (body) => call('/campaigns/build', { method: 'POST', body }),
  previewCampaign: (body) => call('/campaigns/preview', { method: 'POST', body }),
  patchCampaign: (id, body) => call(`/campaigns/${id}`, { method: 'PATCH', body }),
  updateCampaign: (id, body) => call(`/campaigns/${id}`, { method: 'PATCH', body }),
  deleteCampaign: (id) => call(`/campaigns/${id}`, { method: 'DELETE' }),
  setVariant: (id, segment, body) => call(`/campaigns/${id}/variants/${segment}`, { method: 'PUT', body }),
  addMembers: (id, members) => call(`/campaigns/${id}/members`, { method: 'POST', body: { members } }),
  removeMember: (id, key) => call(`/campaigns/${id}/members/${encodeURIComponent(key)}`, { method: 'DELETE' }),
  logOutcome: (id, key, body) => call(`/campaigns/${id}/members/${encodeURIComponent(key)}/outcome`, { method: 'POST', body }),
  addTouch: (body) => call('/touches', { method: 'POST', body }),
  addReply: (body) => call('/replies', { method: 'POST', body }),
  replies: () => call('/replies'),
  setNextStep: (slug, body) => call(`/accounts/${slug}/next-step`, { method: 'PUT', body }),
  setStatus: (slug, status) => call(`/accounts/${slug}/status`, { method: 'PUT', body: { status } }),
  draft: (body) => call('/draft', { method: 'POST', body }),
  upsertContact: (slug, body) => call(`/accounts/${slug}/contacts`, { method: 'POST', body }),
  removeContact: (slug, name) => call(`/accounts/${slug}/contacts/${encodeURIComponent(name)}`, { method: 'DELETE' }),
  setEmailPattern: (slug, body) => call(`/accounts/${slug}/email-pattern`, { method: 'PUT', body }),
  researchLog: () => call('/research/log'),
  queueResearch: (companies, model) => call('/research/queue', { method: 'POST', body: { companies, model } }),
  importResearch: (body) => call('/research/import', { method: 'POST', body }),
  setSchedule: (body) => call('/research/schedule', { method: 'PUT', body }),
  addSource: (body) => call('/intel/sources', { method: 'POST', body }),
  costEstimate: ({ kind, count, model }) =>
    call(`/cost/estimate?kind=${kind}&count=${count}${model ? `&model=${model}` : ''}`),
  runTopup: (body) => call('/research/topup', { method: 'POST', body }),
  runs: () => call('/runs'),
  settings: () => call('/settings'),
  setSettings: (body) => call('/settings', { method: 'PUT', body }),
  costCompare: ({ kind, count }) => call(`/cost/compare?kind=${kind}&count=${count}`),
  setPriority: (slug, priority) => call(`/accounts/${slug}/priority`, { method: 'PUT', body: { priority } }),
  intelSchedule: () => call('/intel/schedule'),
  prompts: () => call('/prompts'),
  prompt: (id) => call(`/prompts/${id}`),
  savePrompt: (id, text) => call(`/prompts/${id}`, { method: 'PUT', body: { text } }),
  revertPrompt: (id) => call(`/prompts/${id}`, { method: 'DELETE' }),
  addCompany: (body) => call('/appendix', { method: 'POST', body }),
  setIntelSchedule: (body) => call('/intel/schedule', { method: 'PUT', body }),
  rescanIntel: (body) => call('/intel/rescan', { method: 'POST', body }),
  setRunSurface: (id, surface) => call(`/runs/${id}/surface`, { method: 'PUT', body: { surface } }),
  health: () => call('/health'),
  me: () => call('/me'),
}

export const fmt = {
  days: (n) => (n == null ? 'n/d' : Number(n).toFixed(1)),
  int: (n) => (n == null ? '—' : String(n)),
  date: (iso) => {
    if (!iso) return '—'
    const d = new Date(`${iso}T00:00:00Z`)
    if (Number.isNaN(d.getTime())) return iso
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
  },
  status: (s) => (s || 'not_started').replace(/_/g, ' ').toUpperCase(),
}
